import { ApiSdk } from '@/httpclient';
import { GuideModel } from '@/__generated';
import { Button, Checkbox, Col, Divider, Form, Input, InputNumber, Modal, Radio, Rate, Row, Select, Spin } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import NationalityInput from '@/components/app/formItem/NationalityInput';
import PhoneNumberInput from '@/components/app/formItem/PhoneNumberInput';
import PrefectureInput from '@/components/app/formItem/PrefectureInput';
import { VALIDATE_REQUIRED, PhoneNumberRegex, AvailableTypes, AvailableArea, GuideRatingType } from '@/constants';
import DatePicker from '@/components/DatePicker';
import GuideAttachment from '../GuidesProfile/components/GuideAttachment';
import { formatApiDate, SupportedLanguageLicenseType } from '@/config';
import { sumBy } from 'lodash';
import FileDownload from '@/components/app/FileDownload';
import { CopyOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { defaultApiErrorHandler, getGuideFullName } from '@/helpers/string';
import moment from 'moment';
import { useAppContext } from '@/contexts/AppContext';
import copy from 'copy-to-clipboard';

type Props = {
  gid: string;
  visible: boolean;
  onClose: (guide?: GuideModel) => void;
  isGuidePicker?: boolean;
  guestNationality?: string;
  tourName?: string;
  tourId?: string;
  firstBusinessDate?: any;
};

export default function GuideInfoModal({ gid, visible, onClose, isGuidePicker, guestNationality, tourName, tourId, firstBusinessDate }: Props) {
  const { t } = useTranslation();
  const { auth } = useAppContext();
  const [onUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [displayModal, setDisplayModal] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [guide, setGuide] = React.useState<GuideModel>();
  const [form] = Form.useForm();
  const [ratingForm] = Form.useForm();

  const { formLayout, labelCol, wrapperCol } = React.useMemo(() => {
    if (window.innerWidth > 800) {
      return {
        formLayout: 'vertical',
        labelCol: undefined,
        wrapperCol: undefined,
      };
    }
    return {
      formLayout: 'horizontal',
      labelCol: { span: 10, sm: 10, xs: 10 },
      wrapperCol: { span: 14, sm: 14, xs: 14 },
    };
  }, []);
  const getGuideInfo = () => {
    setLoading(true);
    ApiSdk.GuideService.getById({
      id: gid,
    })
      .then(res => {
        if (res) {
          setGuide(res);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (gid) {
      getGuideInfo();
    }
  }, [gid]);

  const addNewTourRating = () => {
    ratingForm.validateFields().then(values => {
      setIsUpdating(true);

      ApiSdk.GuideService.addTourGuideRating({
        body: {
          ...values,
          date: formatApiDate(firstBusinessDate),
          guideId: gid,
          type: GuideRatingType.Tour,
          carteName: tourName,
          tourId: tourId,
          nationality: guestNationality,
          reviewerName: auth?.fullname,
          reviewerId: auth?.id,
        },
      })
        .then(res => {
          if (res.success) {
            setDisplayModal(false);
            getGuideInfo();
          } else {
            defaultApiErrorHandler(res);
          }
        })
        .catch(defaultApiErrorHandler)
        .finally(() => {
          setIsUpdating(false);
        });
    });
  };
  let rate = React.useMemo(() => {
    let rates = guide?.ratings || [];
    return parseFloat((sumBy(rates, (x: any) => x.rating || 0) / rates.length).toFixed(2)) || 0;
  }, [guide]);
  return (
    <Modal
      className="nta-custom-form create-tour-form"
      bodyStyle={{ height: 'calc(100vh - 150px)', overflow: 'auto', padding: 20 }}
      width={1000}
      style={{ top: 10 }}
      visible={visible}
      title={'ガイド情報'}
      onOk={() => {
        if (isGuidePicker) {
          onClose(guide);
        } else {
          onClose();
        }
      }}
      onCancel={() => {
        onClose();
      }}
      okText={isGuidePicker ? t('Select Guide') : t('Close')}
      cancelButtonProps={{ style: { display: 'none' } }}
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {guide ? (
          <Form
            form={form}
            initialValues={guide}
            className="guide-info h-adr"
            layout={formLayout as any}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
            labelAlign={'left'}
            scrollToFirstError
          >
            <Row gutter={6} style={{ marginBottom: 5 }}>
              <Col md={10} xs={24}>
                <Row gutter={[6, 6]}>
                  <Col md={24} xs={24}>
                    <h3>プロフィール</h3>
                  </Col>
                  {guide.profileImageId ? (
                    <Col md={24} xs={24}>
                      <GuideAttachment
                        title={t('guideSerchphoto')}
                        fileName={`${process.env.REACT_APP_API_ENDPOINT}/api/cms/file/view/${guide.profileImageId}`}
                      />
                    </Col>
                  ) : (
                    <Col md={24} xs={24}>
                      <b>{t('guideSerchphoto')}</b>
                      <div
                        style={{
                          height: 150,
                          width: '100%',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          borderRadius: 6,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        画像なし
                      </div>
                    </Col>
                  )}

                  {guide.businessCardFrontId ? (
                    <Col md={24} xs={24}>
                      <GuideAttachment
                        title={t('guideSerchbusinessCardFrontPhoto')}
                        fileName={`${process.env.REACT_APP_API_ENDPOINT}/api/cms/file/view/${guide.businessCardFrontId}`}
                      />
                    </Col>
                  ) : (
                    <Col md={24} xs={24}>
                      <b>{t('guideSerchbusinessCardFrontPhoto')}</b>
                      <div
                        style={{
                          height: 150,
                          width: '100%',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          borderRadius: 6,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        画像なし
                      </div>
                    </Col>
                  )}
                  {guide.businessCardBackId ? (
                    <Col md={24} xs={24}>
                      <GuideAttachment
                        title={t('guideSerchbusinessCardBackPhoto')}
                        fileName={`${process.env.REACT_APP_API_ENDPOINT}/api/cms/file/view/${guide.businessCardBackId}`}
                      />
                    </Col>
                  ) : (
                    <Col md={24} xs={24}>
                      <b>{t('guideSerchbusinessCardBackPhoto')}</b>
                      <div
                        style={{
                          height: 150,
                          width: '100%',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          borderRadius: 6,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        画像なし
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col md={14} xs={24}>
                <Row gutter={6}>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchaccountId')} name="accountId">
                      <Input disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Row gutter={6}>
                      <Col md={8} xs={24}>
                        <Form.Item label={t('guideSerchrank')} name="guideRank">
                          <Input disabled={true} className="input-value-color" />
                        </Form.Item>
                      </Col>
                      <Col md={16} xs={24}>
                        <Form.Item label={t('guideSerchstatus')}>
                          <Radio.Group value={guide.enabled} className="input-value-color">
                            <Radio value={true}>{t('staffSearch_active')}</Radio>
                            <Radio value={false}>{t('staffSearch_inactive')}</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label={t('guideSearch_guideName')}>
                      <Input
                        value={getGuideFullName(guide as any)}
                        maxLength={255}
                        disabled={true}
                        className="input-value-color"
                        addonAfter={
                          <CopyOutlined
                            onClick={() => {
                              copy(getGuideFullName(guide as any));
                            }}
                          />
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchlastNameKatagana')} name="lastNameKatakana">
                      <Input maxLength={255} disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchfirstNameKatagana')} id="firstNameKatakana" name="firstNameKatakana">
                      <Input maxLength={255} disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchlastNameEnglish')} id="firstNameKatakana" name="lastNameEnglish">
                      <Input maxLength={255} disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchfirstNameEnglish')} id="firstNameEnglish" name="firstNameEnglish">
                      <Input maxLength={255} disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item name="nickName" label={t('guideSerchnickname')}>
                      <Input maxLength={255} disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchgender')}>
                      <Radio.Group value={guide.gender} className="input-value-color">
                        <Radio value={1}>{t('guideCreateGenderMale')}</Radio>
                        <Radio value={2}>{t('guideCreateGenderFemale')}</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchbirthdate')} name="birthDate">
                      <DatePicker className="w-100 input-value-color" disabled={true} placeholder="" />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchnationality')} name="nationality">
                      <NationalityInput disabled={true} className="input-value-color" />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item
                      name="mobilePhone"
                      rules={[{ pattern: PhoneNumberRegex, message: t('validatorPhoneNumber') }]}
                      label={t('guideSerchphoneNumberMobile')}
                    >
                      <PhoneNumberInput disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      name="homePhone"
                      rules={[{ pattern: PhoneNumberRegex, message: t('validatorPhoneNumber') }]}
                      label={t('guideSerchPhoneNumberHome')}
                    >
                      <PhoneNumberInput disabled={true} />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item
                      label={t('guideSerchemailAddress1')}
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      id="emailAddress1"
                      name="emailAddress1"
                    >
                      <Input type="email" maxLength={255} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item id="emailAddress2" name="emailAddress2" label={t('guideSerchemailAddress2')}>
                      <Input type="email" maxLength={255} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchzipCode')} name="zipCode">
                      <Input maxLength={12} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>

                  <input type="hidden" className="p-country-name" value="Japan" />

                  <Col md={12} xs={24}>
                    <Form.Item label={t('guideSerchaddressPrefecture')} name="prefecture">
                      <PrefectureInput className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item name="address" label="住所">
                      <Input type="text" maxLength={255} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item id="buildingName" name="buildingName" label={t('guideSerchbuildingName')}>
                      <Input maxLength={255} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item id="nearestStation" name="nearestStation" label={t('guideSerchnearestStation')}>
                      <Input maxLength={255} className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item id="trainLine" name="trainLine" label={t('guideSerchtrainLine')}>
                      <Input className="w-100 input-value-color" disabled={true} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col md={24} xs={24}>
                    <h3>口座情報</h3>
                  </Col>
                  <Col md={24} xs={24}>
                    <Row gutter={6}>
                      <Col md={12} xs={24}>
                        <Form.Item label={t('guideSerchbankName')} name="bankName">
                          <Input maxLength={200} className="w-100 input-value-color" disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        <Form.Item label={t('guideSerchbranchName')} name="branchName">
                          <Input maxLength={200} className="w-100 input-value-color" disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        <Form.Item label={t('guideSerchaccountType')}>
                          <Radio.Group className="w-100 input-value-color" value={guide.bankAccountType}>
                            <Radio value={1}>{t('guideSerchaccountTypeUsually')}</Radio>
                            <Radio value={2}>{t('guideSerchaccountTypeCurrent')}</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        <Form.Item label={t('guideSerchaccountNumber')} name="bankAccountNumber">
                          <Input maxLength={20} className="w-100 input-value-color" disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        <Form.Item label={t('guideSerchaccountHolder')} name="bankAccountHolder">
                          <Input autoComplete="off" maxLength={50} className="w-100 input-value-color" disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={24} xs={24}>
                        <Form.Item valuePropName="checked">
                          <Checkbox checked={guide.defaultPaperContract} className="w-100 input-value-color">
                            {t('guideContract_DefaultPaperContract')}
                          </Checkbox>
                        </Form.Item>
                      </Col>
                      <Col md={24} xs={24}>
                        <Form.Item valuePropName="checked">
                          <Checkbox checked={guide.newbie} className="w-100 input-value-color">
                            {t('guideContract_Newbie')}
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider />
            <div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <h3 className="form-label-required">{t('guideSerchsupportedLanguages')}</h3>
                </div>
                <Row gutter={6} className="mb-2">
                  <Col md={5} className="header-languages-first">
                    <h5>{t('guideSerchlanguage')}</h5>
                  </Col>
                  <Col md={3} className="header-languages">
                    <h5>{t('guideSerchmain')}</h5>
                  </Col>
                  <Col md={9} className="header-languages">
                    <h5>{t('guideSerchlicense')}</h5>
                  </Col>
                  <Col md={4} className="header-languages-end">
                    <h5>{t('guideSerchobtainedDate')}</h5>
                  </Col>
                </Row>

                {guide.supportedLanguages?.map((field, index) => (
                  <Row key={index} gutter={6} className="language-row">
                    <Col md={5}>
                      <Form.Item>
                        <Input disabled value={field.languageName} className="w-100 input-value-color" />
                      </Form.Item>
                    </Col>
                    <Col md={3} className="text-center">
                      <Form.Item>
                        <Radio checked={field.isMain} />
                      </Form.Item>
                    </Col>
                    <Col md={9}>
                      <Row gutter={6}>
                        <Col md={14}>
                          <Form.Item rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <Input
                              disabled
                              value={t(SupportedLanguageLicenseType.__customLabel(field.license)) as any}
                              className="w-100 input-value-color"
                            />
                          </Form.Item>
                        </Col>
                        <Col md={10}>
                          <Form.Item>
                            <Input disabled value={field.licenseDetails} className="w-100 input-value-color" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                    <Col md={4}>
                      <Form.Item>
                        <DatePicker disabled value={field.obtainedDate as any} className="w-100 input-value-color" placeholder="" />
                      </Form.Item>
                    </Col>
                    <Col md={3} className="text-center"></Col>
                  </Row>
                ))}
              </div>

              {/*  */}

              <Row gutter={6}>
                <Col md={5}>
                  <Form.Item label={t('guideSerchpersonalBusinessRegistration')}>
                    <Radio.Group value={guide.isPersonalBusinessRegistration}>
                      <Radio value={true}>{t('staffSearch_yes')}</Radio>
                      <Radio value={false}>{t('staffSearch_no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col md={6}>
                  <Form.Item label={t('guideSerchregistrationNumber')} name="personalBusinessRegistrationNumber">
                    <Input disabled className="w-100 input-value-color" />
                  </Form.Item>
                </Col>
                <Col md={7}>
                  <Form.Item label={t('guideSerchmembershipOfProfessionalInstitutions')}>
                    <Checkbox.Group value={guide.professionalInstitutions}>
                      <Checkbox value="JFG">{t('guideSerchJFG')}</Checkbox>
                      <Checkbox value="JGA">{t('guideSerchJGA')}</Checkbox>
                      <Checkbox value="OTHER">{t('guideSerchOther')}</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col md={6}>
                  <Form.Item label="  ">
                    <Input disabled className="w-100 input-value-color" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={6}>
                <Col md={5}>
                  <Form.Item label={t('guideSerchmaxNumberOfNights')} name="maxNumberOfNights">
                    <InputNumber disabled className="w-100 input-value-color" addonAfter={t('guideSerchDay')} />
                  </Form.Item>
                </Col>
                <Col md={4}>
                  <Form.Item label={t('guideSerchUnitExperience')}>
                    <Radio.Group value={!!guide.maxNumber}>
                      <Radio value={true} className="m-0">
                        {t('staffSearch_yes')}
                      </Radio>
                      <Radio value={false} className="m-0">
                        {t('staffSearch_no')}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col md={4}>
                  <Form.Item label={t('guideSerchmaxNumber')} name="maxNumber">
                    <InputNumber disabled className="w-100 input-value-color" addonAfter="台口" />
                  </Form.Item>
                </Col>
                <Col md={5}>
                  <Form.Item label={t('guideSerchinterpretationExperience')}>
                    <Radio.Group value={guide.hasInterpretationExperience}>
                      <Radio value={true}>{t('staffSearch_yes')}</Radio>
                      <Radio value={false}>{t('staffSearch_no')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col md={6}>
                  <Form.Item name="interpretationExperienceDetails">
                    <Input.TextArea className="w-100 input-value-color" disabled />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <Divider />
            <Row gutter={16}>
              <Col md={24} xs={24}>
                <h3>{t('guideSerchsupportedTypes')}</h3>
                <Form.Item labelCol={{ xs: 24 }} wrapperCol={{ xs: 24 }}>
                  <Checkbox.Group value={guide.supportedTypes}>
                    {AvailableTypes.map(availableType => (
                      <Checkbox value={availableType.value} key={availableType.value}>
                        {availableType.label}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              </Col>
              <Col md={24} xs={24}>
                <h3>{t('guideSerchsupportedAreas')}</h3>
                <Form.Item label={t('')} labelCol={{ xs: 24 }} wrapperCol={{ xs: 24 }}>
                  <Checkbox.Group value={guide.supportedAreas}>
                    {AvailableArea.map(availableArea => (
                      <Checkbox value={availableArea.value} key={availableArea.value}>
                        {availableArea.label}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              </Col>
              <Col md={24} xs={24}>
                <Form.Item label={<h3>{t('guideSerchselfIntroduction')}</h3>} name="selfIntroduction" wrapperCol={{ xs: 24 }}>
                  <Input.TextArea disabled className="w-100 input-value-color" rows={5} />
                </Form.Item>
              </Col>
              <Col md={24} xs={24}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, marginBottom: 10 }}>
                  <div>
                    <h3> 海外在留経験</h3>
                    <br /> ※日付が新しい順にご記入ください
                  </div>
                </div>
                {guide.overseaExperiences?.map(field => {
                  return (
                    <Row gutter={6}>
                      <Col md={20} xs={24}>
                        <Row gutter={6}>
                          <Col xs={4} md={0}>
                            期間
                          </Col>
                          <Col md={8} xs={8}>
                            <Form.Item wrapperCol={{ xs: 24 }}>
                              <DatePicker disabled value={field.from as any} className="w-100 input-value-color" placeholder="" />
                            </Form.Item>
                          </Col>
                          <Col md={8} xs={8}>
                            <Form.Item wrapperCol={{ xs: 24 }}>
                              <DatePicker disabled value={field.to as any} className="w-100 input-value-color" placeholder="" />
                            </Form.Item>
                          </Col>
                          <Col md={0} xs={4} style={{ textAlign: 'center' }}></Col>

                          <Col xs={4} md={0}>
                            場所
                          </Col>
                          <Col md={8} xs={20}>
                            <Form.Item wrapperCol={{ xs: 24 }}>
                              <Input value={field.place} className="input-value-color" disabled maxLength={50} />
                            </Form.Item>
                          </Col>
                          <Col md={24} xs={24}>
                            <Form.Item wrapperCol={{ xs: 24 }}>
                              <Input value={field.details} className="input-value-color" disabled maxLength={50} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      <Col md={4} xs={0} style={{ textAlign: 'center' }}></Col>
                    </Row>
                  );
                })}
              </Col>
              <Divider />
              <Col md={12} xs={24}>
                <Form.Item name="resumeUpload" wrapperCol={{ xs: 24 }}>
                  <Row gutter={6}>
                    <Col md={24} style={{ marginBottom: 5 }}>
                      <h3> {t('guideSerchresumeUpload')}</h3>
                    </Col>
                    <Col md={24}>
                      {guide.resumeUpload ? <FileDownload fileName={guide.resumeUpload?.name} fileId={guide.resumeUpload.id} /> : null}
                    </Col>
                  </Row>
                </Form.Item>
              </Col>

              <Col md={12} xs={24}>
                <Form.Item name="cvUpload" wrapperCol={{ xs: 24 }}>
                  <Row gutter={6}>
                    <Col md={24} xs={8} style={{ marginBottom: 5 }}>
                      <h3> {t('guideSerchcVUpload')}</h3>
                    </Col>
                    <Col md={24}>{guide.cvUpload ? <FileDownload fileName={guide.cvUpload.name} fileId={guide.cvUpload.id} /> : null}</Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col md={24} xs={24}>
                <Form.Item name="memo" label={t('guideSerchmemo')} labelCol={{ xs: 24 }} wrapperCol={{ xs: 24 }}>
                  <Input.TextArea disabled className="w-100 input-value-color" />
                </Form.Item>
              </Col>
            </Row>
            {guide.annualWorkDayList?.length ? (
              <>
                <Divider />
                <div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                      <div className="form-label-required" style={{ flex: 1 }}>
                        過去の年間稼働日数
                      </div>
                    </div>
                    <Row gutter={6} className="mb-2">
                      <Col md={6}>
                        <h4>稼働年</h4>
                      </Col>
                      <Col md={6}>
                        <h4>稼働日数</h4>
                      </Col>
                    </Row>

                    {guide.annualWorkDayList?.map((field, index) => (
                      <Row key={index} gutter={6} style={{ marginBottom: 4 }}>
                        <Col md={6}>
                          <InputNumber className="w-100 input-value-color" disabled value={field.year} />
                        </Col>
                        <Col md={6} className="text-center">
                          <InputNumber className="w-100 input-value-color" disabled value={field.workingDay} />
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            <Divider />
            <Row gutter={6}>
              <Col md={24} xs={24}>
                <Form.Item name="internalMemo" label={t('guideSerchinternalMemo')}>
                  <Input.TextArea disabled className="w-100 input-value-color" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />
            <Row style={{ display: 'flex', alignItems: 'center' }}>
              <Col md={8}>
                <h3>{t('guideSerchguideRating')}</h3>
              </Col>
              <Col md={8}>
                <Rate value={rate} disabled allowHalf />
                <span className="text-rating">{rate || ''}</span>
              </Col>
              <Col md={8} style={{ textAlign: 'end' }}>
                {tourId && firstBusinessDate ? (
                  <Button onClick={() => setDisplayModal(true)} type="primary" className="save-btn" icon={<PlusOutlined />}>
                    {t('guideSerchadd')}
                  </Button>
                ) : null}
              </Col>
            </Row>

            <Row>
              {guide.ratings?.map(field => {
                return (
                  <Col md={24} xs={24} key={field.id}>
                    <Row gutter={6}>
                      <Col md={4}>
                        <Form.Item label={field.type === 1 ? t('ratingTourDate') : t('guideSerchdate')}>
                          <DatePicker value={field.date as any} className="w-100 input-value-color" disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={4}>
                        <Form.Item label={t('guideSerchnationality')}>
                          <Input value={field.nationality} className="input-value-color" maxLength={50} disabled={true} />
                        </Form.Item>
                      </Col>

                      <Col md={5}>
                        <Form.Item label={t('guideSerchcarteName')}>
                          <Input value={field.carteName} className="input-value-color" maxLength={50} disabled={true} />
                        </Form.Item>
                      </Col>
                      <Col md={4}>
                        <Form.Item label={t('guideSerchrating')}>
                          <Select disabled value={field.rating} className="input-value-color">
                            <Select.Option value={field.rating}>
                              <Rate value={field.rating} allowHalf={true} disabled style={{ fontSize: 12, color: '#666' }} />
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col md={3}>
                        <Form.Item label={t('guideSerchtype')}>
                          <Input disabled={true} className="input-value-color" value={GuideRatingType.__getLabel(field.type || 0)} />
                        </Form.Item>
                      </Col>

                      <Col md={4}>
                        <Form.Item label={t('guideSerchreviewer')}>
                          <>{field.reviewerName}</>
                        </Form.Item>
                      </Col>
                      <Col md={20}>
                        <Form.Item>
                          <Input.TextArea value={field.details} disabled={true} className="w-100 input-value-color" maxLength={1000} />
                        </Form.Item>
                      </Col>
                      <Col md={4} style={{ textAlign: 'center' }}></Col>
                    </Row>
                  </Col>
                );
              })}
            </Row>
          </Form>
        ) : null}
      </Spin>

      {displayModal ? (
        <Modal
          className="nta-custom-form create-tour-form"
          width={600}
          open={displayModal}
          title={t('guideSerchguideRating')}
          onOk={() => {
            addNewTourRating();
          }}
          onCancel={() => {
            setDisplayModal(false);
          }}
          okButtonProps={{ loading: onUpdating, icon: <SaveOutlined />, className: 'save-btn' }}
          okText={t('Save')}
          cancelText={t('Close')}
          maskClosable={false}
        >
          <Form
            form={ratingForm}
            initialValues={{
              rating: 5,
            }}
            className="nta-custom-form create-tour-form"
            layout="horizontal"
            labelCol={{ md: 6 }}
            wrapperCol={{ md: 18 }}
            labelAlign={'left'}
            scrollToFirstError
          >
            <Form.Item label={t('guideSerchnationality')}>
              <Input disabled={true} className="input-value-color" value={guestNationality} />
            </Form.Item>
            <Form.Item label={t('guideSerchcarteName')}>
              <Input disabled={true} className="input-value-color" value={tourName} />
            </Form.Item>
            <Form.Item label={t('guideSerchtype')}>
              <Input disabled={true} className="input-value-color" value={GuideRatingType.__getLabel(GuideRatingType.Tour)} />
            </Form.Item>
            <Form.Item label={t('ratingTourDate')}>
              <DatePicker disabled value={firstBusinessDate} className="w-100 input-value-color" placeholder={t('ratingTourDate')} />
            </Form.Item>
            <Form.Item label={t('guideSerchrating')} name={'rating'} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
              <Select className="input-value-color">
                {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(rate => {
                  return (
                    <Select.Option value={rate}>
                      <Rate value={rate} allowHalf={true} disabled style={{ fontSize: 12, color: '#666' }} />
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item name={'details'} label={t('guideSerchdetails')}>
              <Input.TextArea className="w-100 input-value-color" placeholder="詳細" maxLength={1000} />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </Modal>
  );
}
